import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FinalCta } from "@/components/FinalCta";
import { SEOHead } from "@/components/SEOHead";
import { websTrabajos, agentesTrabajos } from "@/lib/trabajos";
import { testimoniosData } from "@/components/TestimoniosCarousel";

// ---------------------------------------------------------------------------
// CASOS — "Negocios reales, funcionando hoy." Webs grid + testimonials masonry.
// ---------------------------------------------------------------------------

const testimonials = testimoniosData
  .filter((t) => !t.videoUrl)
  .map((t) => ({
    tag: t.metric,
    quote: t.quote,
    name: t.name,
    company: t.role,
    initials: t.initials,
  }));

const Trabajos = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Casos — Negocios reales funcionando hoy | CreceLocal"
        description="Los sitios web y sistemas que construimos para empresas de servicios y distribuidores. Casos reales, cuentas funcionando, con el testimonio del dueño."
        canonical="/trabajos"
      />
      <Navbar />
      <main>
        <section
          className="mx-auto px-6"
          style={{
            maxWidth: 1240,
            paddingTop: "clamp(72px,9vw,120px)",
            paddingBottom: 40,
          }}
        >
          <div
            className="font-mono text-[13px] text-[#A78BFA]"
            style={{ letterSpacing: "0.08em" }}
          >
            CASOS DE ÉXITO
          </div>
          <h1
            className="font-medium text-[#EEEDF2] mt-5"
            style={{
              fontSize: "clamp(44px,6.6vw,92px)",
              lineHeight: 0.98,
              letterSpacing: "-0.03em",
              maxWidth: 900,
              textWrap: "balance" as never,
            }}
          >
            Negocios reales, funcionando hoy.
          </h1>
          <p
            className="text-[#BAB6C4] mt-5"
            style={{
              fontSize: 18,
              maxWidth: 600,
              lineHeight: 1.55,
            }}
          >
            En la llamada te mostramos estas cuentas en vivo. No una demo armada
            para la ocasión.
          </p>
        </section>

        {/* Webs grid */}
        <section
          className="mx-auto px-6"
          style={{ maxWidth: 1240, paddingTop: 24, paddingBottom: 80 }}
        >
          <div
            className="grid"
            style={{
              gridTemplateColumns:
                "repeat(auto-fit,minmax(min(100%,320px),1fr))",
              gap: 20,
            }}
          >
            {websTrabajos.map((w) => (
              <div
                key={w.client}
                className="border border-[#221F2A] rounded-2xl overflow-hidden bg-[#13111A]"
              >
                <div
                  className="overflow-hidden bg-[#1A1722]"
                  style={{ aspectRatio: "4 / 3" }}
                >
                  <img
                    src={w.image}
                    alt={`${w.client} — ${w.rubro}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="px-5 py-[18px]">
                  <div className="font-semibold text-[#EEEDF2]">{w.client}</div>
                  <div className="text-[14px] text-[#9C98A6] mt-0.5">
                    {w.rubro}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Agentes */}
        <section
          className="mx-auto px-6"
          style={{ maxWidth: 1240, paddingBottom: 80 }}
        >
          <h2
            className="font-medium text-[#EEEDF2]"
            style={{
              fontSize: "clamp(28px,3vw,40px)",
              letterSpacing: "-0.02em",
            }}
          >
            Agentes de IA en producción
          </h2>
          <p className="text-[#9C98A6] mt-2.5" style={{ fontSize: 16 }}>
            Entrenados con la información de cada negocio, atendiendo WhatsApp,
            Instagram y Facebook.
          </p>
          <div
            className="grid mt-8"
            style={{
              gridTemplateColumns:
                "repeat(auto-fit,minmax(min(100%,320px),1fr))",
              gap: 16,
            }}
          >
            {agentesTrabajos.map((a) => (
              <div
                key={a.client}
                className="border border-[#221F2A] rounded-2xl bg-[#13111A] p-6"
              >
                <div className="font-semibold text-[#EEEDF2]">
                  {a.client}{" "}
                  <span className="text-[#9C98A6] font-normal">
                    · {a.rubro}
                  </span>
                </div>
                <p className="text-[#9C98A6] mt-2" style={{ lineHeight: 1.55 }}>
                  {a.queSeHizo}
                </p>
                {a.testimonio && (
                  <div
                    className="mt-3 pl-3.5"
                    style={{ borderLeft: "2px solid rgba(124,58,237,0.4)" }}
                  >
                    <p
                      className="text-[#EEEDF2] italic"
                      style={{ fontSize: 15, lineHeight: 1.5 }}
                    >
                      “{a.testimonio.quote}”
                    </p>
                    <p className="text-[#9C98A6] text-[13px] mt-1.5">
                      — {a.testimonio.name}, {a.testimonio.business}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials masonry */}
        <section className="bg-[#0F0D14] border-t border-[#221F2A]">
          <div
            className="mx-auto px-6"
            style={{ maxWidth: 1240, paddingTop: 80, paddingBottom: 96 }}
          >
            <h2
              className="font-medium text-[#EEEDF2]"
              style={{
                fontSize: "clamp(34px,4vw,52px)",
                letterSpacing: "-0.02em",
              }}
            >
              En sus palabras
            </h2>
            <div
              className="mt-10"
              style={{
                columns: "3 300px",
                columnGap: 16,
              }}
            >
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className="mb-4 border border-[#221F2A] rounded-[14px] p-6 bg-[#0A090D]"
                  style={{ breakInside: "avoid" }}
                >
                  <span
                    className="font-mono text-[11px] text-[#A78BFA]"
                    style={{ letterSpacing: "0.06em" }}
                  >
                    {t.tag}
                  </span>
                  <p
                    className="mt-3 text-[#D8D5E0]"
                    style={{ lineHeight: 1.55, textWrap: "pretty" as never }}
                  >
                    “{t.quote}”
                  </p>
                  <div className="flex gap-2.5 items-center mt-[18px]">
                    <div
                      className="rounded-full grid place-items-center font-semibold text-[#A78BFA]"
                      style={{
                        width: 32,
                        height: 32,
                        background: "#251E3A",
                        fontSize: 12,
                      }}
                    >
                      {t.initials}
                    </div>
                    <div style={{ fontSize: 14 }}>
                      <div className="font-semibold text-[#EEEDF2]">
                        {t.name}
                      </div>
                      <div className="text-[#9C98A6]">{t.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <FinalCta />
      </main>
      <Footer />
    </div>
  );
};

export default Trabajos;
